'use client';

import { ShieldAlert, Timer, XCircle } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { authClient } from '@/lib/auth-client';
import { trpc } from '@/lib/trpc';

export default function ImpersonationBanner() {
  // Gate network call strictly to admins to avoid 403s for other users
  const { data: session } = authClient.useSession();
  const isAdmin = (session as { user?: { role?: string } })?.user?.role === 'admin';

  const { data, error, refetch } = trpc.admin.roleSwitch.getCurrentRoleState.useQuery(undefined, {
    // Only run for admins
    enabled: !!isAdmin,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 10_000,
  });
  const revert = trpc.admin.roleSwitch.revertToAdmin.useMutation({ onSuccess: () => refetch() });
  const utils = trpc.useUtils();

  const invalidateRoleSensitiveQueries = async () => {
    await Promise.allSettled([
      utils.user.progress.getProgress.invalidate(),
      utils.user.billing.getSubscriptionDetails.invalidate(),
      utils.ai.checkTarotLimits.invalidate(),
      utils.ai.getRateLimitStatus.invalidate({ serviceType: 'oracle' }),
    ]);
  };

  const remaining = useMemo(() => {
    if (!data?.isImpersonatingRole) {
      return null;
    }
    const exp = 'exp' in data ? (data.exp as number) : undefined;
    if (!exp) {
      return null;
    }
    const SECOND_MS = 1000;
    const now = Math.floor(Date.now() / SECOND_MS);
    return Math.max(0, exp - now);
  }, [data]);

  useEffect(() => {
    // removed periodic no-op interval; countdown derives from time, no need to tick
  }, []);

  if (error) {
    return null; // Non-admins will hit auth error — hide banner
  }
  if (!data?.isImpersonatingRole) {
    return null;
  }

  const planLabel = (() => {
    if (data.roleOverride === 'logged-out') {
      return 'Logged Out';
    }
    switch (data.subscriptionOverride) {
      case 'cosmic-oracle':
        return 'Oracle (Premium)';
      case 'cosmic-navigator':
        return 'Navigator (Premium)';
      default:
        return 'Stargazer (Free)';
    }
  })();

  const remainingLabel =
    remaining !== null ? `${Math.floor(remaining / 60)}m ${remaining % 60}s` : '';

  return (
    <div className="fixed inset-x-0 top-0 z-[60] flex items-center justify-center">
      <div className="m-2 flex w-[min(920px,95vw)] items-center gap-3 rounded-md border border-purple-500/30 bg-purple-950/60 px-3 py-2 text-purple-100 backdrop-blur">
        <ShieldAlert className="h-4 w-4 text-purple-300" />
        <span className="text-sm">
          Simulating plan: <b>{planLabel}</b>
        </span>
        {remaining !== null && (
          <span className="ml-2 flex items-center gap-1 text-purple-200 text-xs">
            <Timer className="h-3.5 w-3.5" /> {remainingLabel}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button onClick={() => refetch()} size="sm" variant="secondary">
            Refresh
          </Button>
          <Button
            onClick={() => {
              revert.mutate(undefined, { onSuccess: () => invalidateRoleSensitiveQueries() });
            }}
            size="sm"
            variant="destructive"
          >
            <XCircle className="mr-1 h-3.5 w-3.5" /> Exit Simulation
          </Button>
        </div>
      </div>
    </div>
  );
}
